import { useState } from 'react'
import { cn } from '@/lib/utils'
import { useTheme } from '@/theme/useTheme'
import { brandingTokens, contrastRatio } from '@/theme/branding'
import graphitLogo from '@/images/GraphitLogoSmall.png'

export const IDENTITY_TENANT = 'tenant'
/** Graphit's own mark and name, whatever tenant is signed in. */
export const IDENTITY_GRAPHIT = 'graphit'

const GRAPHIT_BRANDING = { application_name: 'Graphit', tenant_id: null, logo_url: null, dark_logo_url: null, icon_url: null }

/**
 * One identity treatment for full, collapsed, mobile and public contexts.
 *
 * `identity="graphit"` shows Graphit's mark and name instead of the current
 * tenant's - for pages where the organization is still being chosen (the
 * Rock link consent page), where a tenant's brand would claim a choice the
 * user has not made.
 */
export default function TenantBrandIdentity({ compact = false, surface = 'default', identity = IDENTITY_TENANT, className, markClassName, nameClassName }) {
  const { branding: themeBranding, resolved } = useTheme()
  const branding = identity === IDENTITY_GRAPHIT ? { ...themeBranding, ...GRAPHIT_BRANDING } : themeBranding
  const name = branding.application_name
  const navigationBackground = surface === 'navigation' ? brandingTokens(branding, resolved)['--color-navigation'] : null
  const darkBackground = navigationBackground
    ? contrastRatio('#ffffff', navigationBackground) > contrastRatio('#000000', navigationBackground)
    : resolved === 'dark'
  const preferredLogo = darkBackground ? branding.dark_logo_url || branding.logo_url : branding.logo_url
  const [failedUrls, setFailedUrls] = useState([])
  const candidates = [compact && branding.icon_url, preferredLogo, branding.logo_url, !branding.tenant_id && graphitLogo].filter(Boolean)
  const logo = candidates.find((url) => !failedUrls.includes(url))
  const initials = name.trim().split(/\s+/).slice(0, 2).map((word) => Array.from(word)[0]).join('').toUpperCase()

  return (
    <span className={cn('inline-flex min-w-0 items-center gap-3', className)}>
      {logo ? (
        <img
          key={logo}
          src={logo}
          alt=""
          aria-hidden="true"
          className={cn(
            'h-9 w-9 shrink-0 rounded-control object-contain',
            // Graphit's own mark is a navy disc: on a dark surface it needs
            // a light rim to separate it from the background.
            darkBackground && logo === graphitLogo && 'rounded-full ring-2 ring-ink/70',
            markClassName
          )}
          onError={() => setFailedUrls((previous) => [...previous, logo])}
        />
      ) : (
        <span
          aria-hidden="true"
          className={cn('flex h-9 w-9 shrink-0 items-center justify-center rounded-control bg-brand text-sm font-semibold text-brand-fg', markClassName)}
        >
          {initials || 'G'}
        </span>
      )}
      <span className={cn(compact ? 'sr-only' : 'min-w-0 truncate text-sm font-semibold', nameClassName)}>
        {name}
      </span>
    </span>
  )
}
